import { useState } from 'react';

interface FaqItemProps {
  question: string;
  answer: React.ReactNode;
  icon: string;
  defaultOpen?: boolean;
}

function FaqItem({ question, answer, icon, defaultOpen = false }: FaqItemProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-background-200/70 last:border-b-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left cursor-pointer group"
      >
        <span className="flex items-center gap-3 pr-4">
          <span className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-100 text-accent-500 flex-shrink-0">
            <i className={icon}></i>
          </span>
          <span className="font-heading text-sm md:text-base font-bold text-foreground-800 group-hover:text-primary-500 transition-colors duration-150">
            {question}
          </span>
        </span>
        <span className={`w-6 h-6 flex items-center justify-center flex-shrink-0 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}>
          <i className="ri-arrow-down-s-line text-foreground-400 text-lg"></i>
        </span>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          open ? 'max-h-[800px] opacity-100 pb-5' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="pl-12 text-sm text-foreground-600 leading-relaxed pr-4">
          {answer}
        </div>
      </div>
    </div>
  );
}

const faqs = [
  {
    question: 'How do I place an order?',
    icon: 'ri-shopping-bag-line',
    defaultOpen: true,
    answer: (
      <div className="space-y-3">
        <p>Placing an order on PounceBounce is simple — and it all happens through WhatsApp, so you&rsquo;re talking to a real person, not a checkout robot. Here&rsquo;s the step-by-step:</p>
        <ol className="list-decimal pl-5 space-y-2">
          <li><strong>Browse &amp; Add to Cart:</strong> Explore our Shop, find what your pet will love, and click &ldquo;Add to Cart&rdquo; on any product. You can add multiple items, choose variants (like collar sizes), and adjust quantities.</li>
          <li><strong>Review Your Cart:</strong> Click the cart icon in the top-right corner to open the quick drawer, or visit the full Cart page for a detailed view with quantity controls.</li>
          <li><strong>Fill in Your Details:</strong> On the Cart page, enter your name, phone number, and delivery address. You can also add order notes — like gifting instructions or delivery preferences.</li>
          <li><strong>Send via WhatsApp:</strong> Tap the green &ldquo;Send Order via WhatsApp&rdquo; button. This opens a WhatsApp chat with a pre-filled message containing your entire order summary — items, quantities, prices, and your delivery details.</li>
          <li><strong>Confirm &amp; Relax:</strong> Hit send in WhatsApp. Our team will reply (typically within 2 hours during business hours) to confirm your order, finalise any details, and arrange delivery.</li>
        </ol>
        <p className="flex items-center gap-2 p-3 bg-secondary-50 rounded-lg">
          <i className="ri-lightbulb-line text-secondary-500 flex-shrink-0"></i>
          <span className="text-xs">No payment is collected on this website. All payment and delivery confirmation happens directly over WhatsApp — safe, personal, and simple.</span>
        </p>
      </div>
    ),
  },
  {
    question: 'Are your materials really safe for my pet?',
    icon: 'ri-shield-check-line',
    answer: (
      <div className="space-y-3">
        <p>Yes — and we don&rsquo;t use that word lightly. Every material we use is chosen specifically because it&rsquo;s non-toxic and safe for pets who chew, lick, and carry things in their mouths all day.</p>
        <ul className="space-y-2">
          <li className="flex items-start gap-2">
            <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-footprint-line text-accent-500 text-xs"></i>
            </span>
            <span><strong>Natural Rubber:</strong> BPA-free, PVC-free, phthalate-free. Lab-tested for heavy metals. Biodegradable.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-footprint-line text-accent-500 text-xs"></i>
            </span>
            <span><strong>Jute &amp; Hemp:</strong> Naturally antimicrobial. Shed fibres are plant matter — safely digestible in small amounts. Zero synthetic dyes.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-footprint-line text-accent-500 text-xs"></i>
            </span>
            <span><strong>Coconut Coir &amp; Bamboo:</strong> No chemical processing. Fully biodegradable. Locally sourced in India.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-footprint-line text-accent-500 text-xs"></i>
            </span>
            <span><strong>Organic Cotton &amp; Silicone:</strong> Food-grade silicone (BPA-free). Organic cotton with natural plant dyes. Machine-washable.</span>
          </li>
        </ul>
        <p>Every batch is tested before it ships. If it doesn&rsquo;t pass, it doesn&rsquo;t go out. Read more on our <a href="/about" className="text-primary-500 hover:underline font-medium">About page</a>.</p>
      </div>
    ),
  },
  {
    question: 'How do I choose the right size for my pet?',
    icon: 'ri-ruler-line',
    answer: (
      <div className="space-y-3">
        <p>Sizing depends on the product — here&rsquo;s a quick guide:</p>
        <ul className="space-y-3">
          <li>
            <strong className="text-foreground-800">Collars:</strong> Measure your pet&rsquo;s neck where the collar sits (mid-neck) with a soft measuring tape. Add 2 fingers&rsquo; width for comfort. If between sizes, size up for growing pups.
          </li>
          <li>
            <strong className="text-foreground-800">Bandanas:</strong> Our sizes are based on neck circumference. S fits up to 35cm, M up to 50cm, L up to 65cm. Tie loosely — you should fit two fingers between the bandana and your pet&rsquo;s neck.
          </li>
          <li>
            <strong className="text-foreground-800">Chew Toys:</strong> Our chew toys are sized by chew strength, not breed size. Puppies and seniors should use our lighter-chew options (Coco Stick, SoftBite Ring). Adult aggressive chewers should go for the Forest Bite Bone or TreatHunt Puzzle.
          </li>
        </ul>
        <p>Still unsure? WhatsApp us — we&rsquo;re happy to help you pick the right size for your specific pet.</p>
      </div>
    ),
  },
  {
    question: 'How do I clean and care for PounceBounce products?',
    icon: 'ri-drop-line',
    answer: (
      <div className="space-y-3">
        <p>Most PounceBounce products are surprisingly low-maintenance — here&rsquo;s the quick version:</p>
        <ul className="space-y-2">
          <li className="flex items-start gap-2">
            <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-footprint-line text-accent-500 text-xs"></i>
            </span>
            <span><strong>Natural Rubber &amp; Silicone:</strong> Hand-wash with warm water and mild soap. Dishwasher safe (top rack).</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-footprint-line text-accent-500 text-xs"></i>
            </span>
            <span><strong>Jute, Hemp &amp; Rope:</strong> Spot clean with a damp cloth. Air dry fully. Avoid machine washing.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-footprint-line text-accent-500 text-xs"></i>
            </span>
            <span><strong>Organic Cotton:</strong> Machine wash cold, gentle cycle. Air dry or tumble dry low. Gets softer with every wash.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-footprint-line text-accent-500 text-xs"></i>
            </span>
            <span><strong>Bamboo &amp; Coir:</strong> Wipe clean with a dry cloth. Keep dry when not in use.</span>
          </li>
        </ul>
        <p>Every product page has detailed care instructions specific to that item. Always inspect toys regularly and replace when heavily worn.</p>
      </div>
    ),
  },
  {
    question: 'What is your returns and replacement policy?',
    icon: 'ri-arrow-go-back-line',
    answer: (
      <div className="space-y-3">
        <p>We stand behind every product we make. If something arrives damaged or isn&rsquo;t what you expected, we&rsquo;ll make it right.</p>
        <ul className="space-y-2">
          <li className="flex items-start gap-2">
            <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-footprint-line text-accent-500 text-xs"></i>
            </span>
            <span><strong>Damaged on arrival:</strong> Send us a photo on WhatsApp within 48 hours of delivery and we&rsquo;ll arrange a free replacement.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-footprint-line text-accent-500 text-xs"></i>
            </span>
            <span><strong>Wrong item or size:</strong> We&rsquo;ll exchange it at no extra cost. Just WhatsApp us within 7 days.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-footprint-line text-accent-500 text-xs"></i>
            </span>
            <span><strong>Normal wear and tear:</strong> Our toys are built to last, but no toy is indestructible. Regular inspection and timely replacement are part of responsible pet parenting.</span>
          </li>
        </ul>
        <p>Since we don&rsquo;t collect payments on this website, all return/replacement coordination happens directly over WhatsApp. We keep it personal and hassle-free.</p>
      </div>
    ),
  },
  {
    question: 'How long does delivery take?',
    icon: 'ri-truck-line',
    answer: (
      <div className="space-y-3">
        <p>Typical delivery timeline is <strong>5-7 business days</strong> across India. Exact timelines depend on your location and will be confirmed over WhatsApp when we process your order.</p>
        <ul className="space-y-2">
          <li className="flex items-start gap-2">
            <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-footprint-line text-accent-500 text-xs"></i>
            </span>
            <span><strong>Metro cities</strong> (Mumbai, Delhi, Bangalore, Chennai, Kolkata, Hyderabad): Usually 3-5 business days.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-footprint-line text-accent-500 text-xs"></i>
            </span>
            <span><strong>Tier-2 cities and rest of India:</strong> Usually 5-7 business days.</span>
          </li>
        </ul>
        <p>Delivery charges (if any) will be communicated before we confirm your order on WhatsApp.</p>
      </div>
    ),
  },
  {
    question: 'Do you ship internationally?',
    icon: 'ri-global-line',
    answer: (
      <div className="space-y-3">
        <p>Currently, we only ship within India. We&rsquo;re a young brand focused on getting our supply chain right at home before expanding.</p>
        <p>If you&rsquo;re outside India and interested in PounceBounce products, drop us a message on WhatsApp or Instagram — we&rsquo;d love to hear from you, and it helps us prioritise which countries to expand to next.</p>
      </div>
    ),
  },
  {
    question: 'How do I know which chew strength is right for my dog?',
    icon: 'ri-psychotherapy-line',
    answer: (
      <div className="space-y-3">
        <p>Great question — it&rsquo;s one of the most common ones we get. Here&rsquo;s our simple guide:</p>
        <ul className="space-y-2">
          <li className="flex items-start gap-2">
            <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-footprint-line text-accent-500 text-xs"></i>
            </span>
            <span><strong>Puppies &amp; Seniors (Light Chewers):</strong> Coco Stick Light Chew, SoftBite Teething Ring, Senior Snuggle Lick Mat. Gentle on developing or ageing teeth.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-footprint-line text-accent-500 text-xs"></i>
            </span>
            <span><strong>Adult Moderate Chewers:</strong> Jute Twist Dental Rope, Bounce Fetch Ball, TreatHunt Puzzle Chew. Durable but not overbuilt.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-footprint-line text-accent-500 text-xs"></i>
            </span>
            <span><strong>Adult Aggressive Chewers:</strong> Forest Bite Rubber Chew Bone. Our toughest toy, built for dogs who destroy everything else.</span>
          </li>
        </ul>
        <p>Always supervise your pet during play, regardless of chew strength. No toy is 100% indestructible — and that&rsquo;s a good thing, because a truly indestructible toy would likely be unsafe.</p>
      </div>
    ),
  },
  {
    question: 'Is my personal information safe?',
    icon: 'ri-lock-line',
    answer: (
      <div className="space-y-3">
        <p>Absolutely. Here&rsquo;s what you need to know:</p>
        <ul className="space-y-2">
          <li className="flex items-start gap-2">
            <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-footprint-line text-accent-500 text-xs"></i>
            </span>
            <span>We <strong>never</strong> collect or store payment information — because we don&rsquo;t process payments on this website at all.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-footprint-line text-accent-500 text-xs"></i>
            </span>
            <span>The name, phone number, and address you enter at checkout are used only to compose your WhatsApp order message and are not stored on any server.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-footprint-line text-accent-500 text-xs"></i>
            </span>
            <span>We don&rsquo;t sell, share, or analyse your data for advertising. We&rsquo;re pet people, not data brokers.</span>
          </li>
        </ul>
      </div>
    ),
  },
];

export default function ContactPage() {
  return (
    <main className="bg-background-50">
      {/* Hero strip */}
      <section className="relative w-full bg-secondary-100 pt-20 pb-10 md:pb-14">
        <div className="w-full px-4 md:px-8 lg:px-12 text-center">
          <div className="w-14 h-14 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-4">
            <i className="ri-chat-smile-2-line text-accent-500 text-2xl"></i>
          </div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-800 mb-3">
            We&rsquo;re All Ears (and Paws)
          </h1>
          <p className="text-foreground-500 text-sm md:text-base max-w-lg mx-auto">
            Questions about materials? Unsure about sizing? Want to say hi? We&rsquo;re a WhatsApp message away — and we actually reply.
          </p>
        </div>
      </section>

      {/* Contact Cards */}
      <section className="w-full px-4 md:px-8 lg:px-12 -mt-6 pb-8">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* WhatsApp */}
            <a
              href="https://wa.me/91XXXXXXXXXX?text=Hi%20PounceBounce!%20I%20have%20a%20question%20about%20your%20products."
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-background-50 rounded-2xl p-5 flex flex-col items-center text-center card-hover-lift border border-background-200/50"
            >
              <div className="w-12 h-12 rounded-xl bg-[#25D366]/10 flex items-center justify-center mb-3 group-hover:bg-[#25D366]/20 transition-colors duration-200">
                <i className="ri-whatsapp-fill text-[#25D366] text-xl"></i>
              </div>
              <h3 className="font-heading text-sm font-bold text-foreground-800 mb-1">WhatsApp</h3>
              <p className="text-xs text-foreground-500">Chat with us directly — fastest way to reach the team</p>
              <span className="text-xs text-[#25D366] font-medium mt-2 flex items-center gap-1">
                Start Chat <i className="ri-arrow-right-up-line text-xs"></i>
              </span>
            </a>

            {/* Email */}
            <div className="bg-background-50 rounded-2xl p-5 flex flex-col items-center text-center border border-background-200/50">
              <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center mb-3">
                <i className="ri-mail-line text-primary-500 text-xl"></i>
              </div>
              <h3 className="font-heading text-sm font-bold text-foreground-800 mb-1">Email</h3>
              <p className="text-xs text-foreground-500">Prefer email? We read every single one</p>
              <span className="text-xs text-primary-500 font-medium mt-2">hello@pouncebounce.in</span>
            </div>

            {/* Instagram */}
            <a
              href="#"
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-background-50 rounded-2xl p-5 flex flex-col items-center text-center card-hover-lift border border-background-200/50"
            >
              <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center mb-3 group-hover:bg-accent-200/50 transition-colors duration-200">
                <i className="ri-instagram-line text-accent-500 text-xl"></i>
              </div>
              <h3 className="font-heading text-sm font-bold text-foreground-800 mb-1">Instagram</h3>
              <p className="text-xs text-foreground-500">Behind-the-scenes, new drops, and way too many dog photos</p>
              <span className="text-xs text-accent-500 font-medium mt-2 flex items-center gap-1">
                @PounceBounce <i className="ri-arrow-right-up-line text-xs"></i>
              </span>
            </a>

            {/* YouTube */}
            <a
              href="#"
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-background-50 rounded-2xl p-5 flex flex-col items-center text-center card-hover-lift border border-background-200/50"
            >
              <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center mb-3 group-hover:bg-accent-200/50 transition-colors duration-200">
                <i className="ri-youtube-line text-accent-500 text-xl"></i>
              </div>
              <h3 className="font-heading text-sm font-bold text-foreground-800 mb-1">YouTube</h3>
              <p className="text-xs text-foreground-500">Product demos, pet care tips, and unboxing joy</p>
              <span className="text-xs text-accent-500 font-medium mt-2 flex items-center gap-1">
                @PounceBounce <i className="ri-arrow-right-up-line text-xs"></i>
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-16">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-accent-500 uppercase tracking-widest mb-3">
              <i className="ri-question-answer-line"></i> Frequently Asked
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-800 mb-3">
              Everything You&rsquo;re Wondering
            </h2>
            <p className="text-foreground-500 text-sm max-w-md mx-auto">
              From ordering to materials to delivery — here&rsquo;s the full scoop. Don&rsquo;t see your question? WhatsApp us.
            </p>
          </div>

          <div className="bg-background-50 rounded-2xl border border-background-200/50 overflow-hidden">
            {faqs.map((faq, i) => (
              <FaqItem
                key={i}
                question={faq.question}
                answer={faq.answer}
                icon={faq.icon}
                defaultOpen={faq.defaultOpen}
              />
            ))}
          </div>

          {/* Still have questions CTA */}
          <div className="mt-10 text-center p-8 bg-secondary-100 rounded-2xl">
            <div className="w-12 h-12 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-3">
              <i className="ri-question-mark text-accent-500 text-xl"></i>
            </div>
            <h3 className="font-heading text-lg font-bold text-foreground-800 mb-2">Still have questions?</h3>
            <p className="text-sm text-foreground-500 mb-5 max-w-sm mx-auto">
              We&rsquo;re real people who love talking about pet products. No chatbots, no ticket systems — just us and you.
            </p>
            <a
              href="https://wa.me/91XXXXXXXXXX?text=Hi%20PounceBounce!%20I%20have%20a%20question."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#25D366] text-white rounded-full font-medium text-sm hover:bg-[#22c35e] transition-colors duration-200 whitespace-nowrap cursor-pointer group"
            >
              <i className="ri-whatsapp-fill text-lg transition-transform duration-300 group-hover:scale-110"></i>
              Chat with Us on WhatsApp
            </a>
          </div>
        </div>
      </section>

      <style>{`
        .card-hover-lift {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .card-hover-lift:hover {
          transform: translateY(-4px);
        }
      `}</style>
    </main>
  );
}